import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VolunteerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Check authentication and fetch user data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data.role !== 'volunteer') {
          navigate('/auth');
          return;
        }
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/auth');
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // State management
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'profile'

  // Unified Workflow State
  const [openListings, setOpenListings] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [otpInput, setOtpInput] = useState('');
  
  // NGO Selection State
  const [showNGOModal, setShowNGOModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [availableNGOs, setAvailableNGOs] = useState([]);
  const [selectedNGO, setSelectedNGO] = useState(null);

  // Profile State
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    country: '',
    vehicle: 'bicycle',
    coverage_km: 10,
    availability_status: 'ACTIVE'
  });
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/volunteer/profile`, {
        withCredentials: true
      });
      setProfile(response.data);
      setProfileForm({
        name: user?.name || '',
        phone: user?.phone || '',
        email: user?.email || '',
        city: response.data.city || '',
        country: response.data.country || '',
        vehicle: response.data.vehicle || 'bicycle',
        coverage_km: response.data.coverage_km || 10,
        availability_status: response.data.availability_status || 'ACTIVE'
      });
      if (response.data.profile_photo_url) {
        setProfilePhotoPreview(`${BACKEND_URL}${response.data.profile_photo_url}`);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  }, [user]);

  // Load data on mount
  useEffect(() => {
    if (user) {
      loadOpenListings();
      loadMyAssignments();
      loadProfile();
    }
  }, [user, loadProfile]);

  const loadOpenListings = async () => {
    try {
      const response = await axios.get(`${API}/workflow/listings/open`, {
        withCredentials: true
      });
      setOpenListings(response.data);
    } catch (error) {
      console.error('Failed to load open listings:', error);
    }
  };

  const loadMyAssignments = async () => {
    try {
      const response = await axios.get(`${API}/workflow/assignments/my`, {
        withCredentials: true
      });
      setMyAssignments(response.data);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
  };

  const handleClaimListing = async (listing) => {
    // In the new two-step workflow, the NGO is already assigned to the listing
    // So we can directly claim without the NGO selection modal
    
    if (!listing.can_claim) {
      toast.error("This listing must be claimed by an NGO first before you can deliver it");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/workflow/listings/${listing.id}/claim`,
        {},  // No body needed - NGO is already assigned
        { withCredentials: true }
      );
      toast.success(`Listing claimed! Delivering to ${response.data.ngo_name}. Your OTP is: ${response.data.otp_code}`);
      loadOpenListings();
      loadMyAssignments();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to claim listing";
      toast.error(errorMsg);
    }
  };

  // Keep legacy modal handlers for backward compatibility (in case old listings exist)
  const handleConfirmClaim = async () => {
    if (!selectedNGO) {
      toast.error("Please select an NGO");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/workflow/listings/${selectedListing.id}/claim`,
        { ngo_id: selectedNGO.id },
        { withCredentials: true }
      );
      toast.success(`Listing claimed! Delivering to ${response.data.ngo_name}. Your OTP is: ${response.data.otp_code}`);
      setShowNGOModal(false);
      setSelectedListing(null);
      setSelectedNGO(null);
      loadOpenListings();
      loadMyAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim listing");
    }
  };

  const handleTaskAction = async (assignmentId, action, additionalData = {}) => {
    try {
      const endpoint = `${API}/workflow/assignments/${assignmentId}/${action}`;
      await axios.post(endpoint, additionalData, { withCredentials: true });
      toast.success(`Task ${action} completed successfully`);
      loadMyAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  // Profile handlers
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only PNG and JPG images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/volunteer/profile/photo/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfilePhotoPreview(`${BACKEND_URL}${response.data.photo_url}`);
      toast.success('Profile photo uploaded successfully');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = async () => {
    try {
      await axios.delete(`${API}/volunteer/profile/photo/remove`, {
        withCredentials: true
      });
      setProfilePhotoPreview(null);
      toast.success('Profile photo removed successfully');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleProfileUpdate = async () => {
    try {
      await axios.put(`${API}/volunteer/profile`, profileForm, {
        withCredentials: true
      });
      toast.success('Profile updated successfully');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleAvailabilityStatusUpdate = async (status) => {
    try {
      await axios.put(`${API}/volunteer/availability-status`, 
        { availability_status: status },
        { withCredentials: true }
      );
      setProfileForm({ ...profileForm, availability_status: status });
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // Removed old mock data and task functions - using real workflow now

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'volunteer') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome, {user.name?.split(' ')[0] || 'Volunteer'}! 👋
              </h1>
              <p className="text-gray-600">
                {currentView === 'dashboard' 
                  ? 'Ready to make a difference today? Claim meals and complete deliveries below.'
                  : 'Manage your profile and account settings'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentView('dashboard')}
                variant={currentView === 'dashboard' ? 'default' : 'outline'}
                className={currentView === 'dashboard' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                Dashboard
              </Button>
              <Button
                onClick={() => setCurrentView('profile')}
                variant={currentView === 'profile' ? 'default' : 'outline'}
                className={currentView === 'profile' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                Profile & Settings
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {currentView === 'dashboard' ? (
          /* Unified Workflow View */
          <div className="space-y-6">
            {/* Open Listings */}
            <Card>
              <CardHeader>
                <CardTitle>Available Listings in {user?.city}</CardTitle>
                <CardDescription>Claim listings to start your pickup and delivery tasks</CardDescription>
              </CardHeader>
              <CardContent>
                {openListings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No open listings available in your city right now</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {openListings.map((listing) => (
                      <Card 
                        key={listing.id} 
                        className={`border-2 ${listing.can_claim ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}
                      >
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {listing.meal_name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                📍 {listing.restaurant_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {listing.portions} portions • {listing.pickup_point}
                              </p>
                              
                              {/* Show NGO info if claimed by NGO */}
                              {listing.ngo && (
                                <div className="mt-2 p-2 bg-green-100 rounded-md">
                                  <p className="text-sm font-medium text-green-800">
                                    🏢 Deliver to: {listing.ngo.name}
                                  </p>
                                  <p className="text-xs text-green-700">
                                    📍 {listing.ngo.address}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={
                                listing.can_claim 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }>
                                {listing.awaiting_ngo_claim ? 'Awaiting NGO' : 'Ready for Delivery'}
                              </Badge>
                              
                              {listing.can_claim ? (
                                <Button
                                  onClick={() => handleClaimListing(listing)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  🚚 Claim & Deliver
                                </Button>
                              ) : (
                                <div className="text-xs text-orange-600 text-right max-w-[150px]">
                                  An NGO must claim this meal first
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Prepared:</span>{' '}
                              {new Date(listing.prepared_at).toLocaleString()}
                            </div>
                            <div>
                              <span className="font-medium">Fresh Till:</span>{' '}
                              {new Date(listing.fresh_till).toLocaleString()}
                            </div>
                            {listing.notes && (
                              <div className="col-span-2">
                                <span className="font-medium">Notes:</span> {listing.notes}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Assignments */}
            <Card>
              <CardHeader>
                <CardTitle>My Assignments</CardTitle>
                <CardDescription>Track your ongoing pickups and deliveries</CardDescription>
              </CardHeader>
              <CardContent>
                {myAssignments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No assignments yet. Claim a listing above to get started!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myAssignments.map((assignment) => (
                      <Card key={assignment.id} className="border-2 border-blue-200">
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {assignment.listing?.meal_name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                📍 Pickup: {assignment.restaurant_address}
                              </p>
                              <p className="text-sm text-green-700 font-medium mt-1">
                                🏢 Deliver to: {assignment.ngo_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                📫 {assignment.ngo_address}
                              </p>
                              <p className="text-sm font-medium text-blue-600">
                                Status: {assignment.assignment_status}
                              </p>
                              {assignment.otp_code && (
                                <p className="text-sm font-bold text-green-600">
                                  OTP: {assignment.otp_code}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Task Action Buttons */}
                          <div className="flex flex-wrap gap-2 mt-4">
                            {assignment.assignment_status === 'SCHEDULED' && (
                              <Button
                                onClick={() => handleTaskAction(assignment.id, 'start')}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Start Pickup
                              </Button>
                            )}
                            {assignment.assignment_status === 'IN_PROGRESS' && !assignment.arrived_at && (
                              <Button
                                onClick={() => handleTaskAction(assignment.id, 'arrive')}
                                className="bg-yellow-600 hover:bg-yellow-700"
                              >
                                Mark Arrival
                              </Button>
                            )}
                            {assignment.assignment_status === 'IN_PROGRESS' && assignment.arrived_at && !assignment.picked_at && (
                              <Button
                                onClick={() => handleTaskAction(assignment.id, 'collect')}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                Mark Collected
                              </Button>
                            )}
                            {assignment.assignment_status === 'IN_PROGRESS' && assignment.picked_at && (
                              <>
                                <div className="flex gap-2 items-center w-full">
                                  <Input
                                    placeholder="Enter OTP"
                                    value={otpInput}
                                    onChange={(e) => setOtpInput(e.target.value)}
                                    className="max-w-32"
                                  />
                                  <Button
                                    onClick={() => {
                                      handleTaskAction(assignment.id, 'verify-otp', { otp: otpInput });
                                      setOtpInput('');
                                    }}
                                    className="bg-indigo-600 hover:bg-indigo-700"
                                  >
                                    Verify OTP
                                  </Button>
                                  <Button
                                    onClick={() => handleTaskAction(assignment.id, 'deliver')}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    Mark Delivered
                                  </Button>
                                </div>
                              </>
                            )}
                            {assignment.assignment_status === 'DELIVERED' && (
                              <Button
                                onClick={() => handleTaskAction(assignment.id, 'complete')}
                                className="bg-gray-600 hover:bg-gray-700"
                              >
                                Complete Task
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Profile & Settings View */
          <div className="space-y-6">
            {/* SECTION 0 - Profile Photo */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
                <CardDescription>Upload your profile picture (PNG/JPG, max 5MB)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border-4 border-gray-300">
                      {profilePhotoPreview ? (
                        <img src={profilePhotoPreview} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-20 h-20 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <label htmlFor="photo-upload">
                      <Button
                        as="span"
                        disabled={uploadingPhoto}
                        className="bg-green-600 hover:bg-green-700 cursor-pointer"
                      >
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                    {profilePhotoPreview && (
                      <Button
                        variant="outline"
                        onClick={handlePhotoRemove}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove Photo
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 1 - Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <Input
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <Input
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <Input
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      placeholder="Your email"
                      type="email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <Input
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      placeholder="Your city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <Input
                      value={profileForm.country}
                      onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                      placeholder="Your country"
                    />
                  </div>
                  <Button onClick={handleProfileUpdate} className="bg-green-600 hover:bg-green-700">
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 2 - Volunteering Details */}
            <Card>
              <CardHeader>
                <CardTitle>Volunteering Details</CardTitle>
                <CardDescription>Configure your volunteering preferences</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                    <select
                      value={profileForm.vehicle}
                      onChange={(e) => setProfileForm({ ...profileForm, vehicle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="scooter">Scooter</option>
                      <option value="bike">Bike</option>
                      <option value="car">Car</option>
                      <option value="van">Van</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Radius (km)</label>
                    <Input
                      type="number"
                      value={profileForm.coverage_km}
                      onChange={(e) => setProfileForm({ ...profileForm, coverage_km: parseInt(e.target.value) })}
                      placeholder="Coverage radius in kilometers"
                    />
                  </div>
                  <Button onClick={handleProfileUpdate} className="bg-green-600 hover:bg-green-700">
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 3 - Volunteer Status */}
            <Card>
              <CardHeader>
                <CardTitle>Volunteer Status</CardTitle>
                <CardDescription>Set your current availability status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div
                    onClick={() => handleAvailabilityStatusUpdate('ACTIVE')}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      profileForm.availability_status === 'ACTIVE'
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">🟢 Active</h4>
                        <p className="text-sm text-gray-600">Available for new assignments</p>
                      </div>
                      {profileForm.availability_status === 'ACTIVE' && (
                        <div className="text-green-600 text-2xl">✓</div>
                      )}
                    </div>
                  </div>
                  <div
                    onClick={() => handleAvailabilityStatusUpdate('BUSY')}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      profileForm.availability_status === 'BUSY'
                        ? 'border-yellow-600 bg-yellow-50'
                        : 'border-gray-200 hover:border-yellow-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">🟡 Busy</h4>
                        <p className="text-sm text-gray-600">Currently occupied, unavailable</p>
                      </div>
                      {profileForm.availability_status === 'BUSY' && (
                        <div className="text-yellow-600 text-2xl">✓</div>
                      )}
                    </div>
                  </div>
                  <div
                    onClick={() => handleAvailabilityStatusUpdate('OFFLINE')}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      profileForm.availability_status === 'OFFLINE'
                        ? 'border-gray-600 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">⚫ Offline</h4>
                        <p className="text-sm text-gray-600">Not available for assignments</p>
                      </div>
                      {profileForm.availability_status === 'OFFLINE' && (
                        <div className="text-gray-600 text-2xl">✓</div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 5 - Account Security */}
            <Card>
              <CardHeader>
                <CardTitle>Account Security</CardTitle>
                <CardDescription>Manage your account settings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full"
                  >
                    Log Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* NGO Selection Modal */}
        {showNGOModal && selectedListing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Select Delivery Destination</h3>
                <Button variant="outline" onClick={() => setShowNGOModal(false)}>×</Button>
              </div>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900">{selectedListing.meal_name}</h4>
                <p className="text-sm text-gray-600">
                  📍 Pickup from: {selectedListing.restaurant_name}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedListing.portions} portions • {selectedListing.city}
                </p>
              </div>

              <h4 className="font-medium text-gray-900 mb-3">Available NGOs in {selectedListing.city}:</h4>
              
              <div className="space-y-3">
                {availableNGOs.map((ngo) => (
                  <div
                    key={ngo.id}
                    onClick={() => setSelectedNGO(ngo)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedNGO?.id === ngo.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h5 className="font-semibold text-gray-900">{ngo.name}</h5>
                          {ngo.recommended && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                              ⭐ Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">📫 {ngo.address}</p>
                        <p className="text-xs text-gray-500 mt-1">📧 {ngo.email}</p>
                        {ngo.phone && (
                          <p className="text-xs text-gray-500">📞 {ngo.phone}</p>
                        )}
                      </div>
                      {selectedNGO?.id === ngo.id && (
                        <div className="text-green-600 text-2xl">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowNGOModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmClaim}
                  disabled={!selectedNGO}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Confirm & Claim Listing
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  };

export default VolunteerDashboard;
